// hooks/sale/useCreditNoteSaleLookup.ts
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import useLoading from '../useLoading';
import useAlerts from '../useAlerts';
import {
    setFilteredDocuments,
    setLookupPaginator,
    clearFilteredDocuments,
    clearSelectedSaleDetail,
    updateProductsFromBalance,
} from '../../store/slices/sale/creditNoteSaleLookupSlice';
import CreditNoteSaleLookupService from '../../services/sale/CreditNoteSaleLookupService';
import { CreditNoteSaleLookupFilter } from '../../types/sale/creditNote/CreditNoteSaleLookup';
import { mapToProductSummaries } from '../../types/sale/creditNote/mapToProductSummaries';
import { setCreditFiscalId } from '../../store/slices/sale/saleSlice';
import useApiErrorHandler from '../useApiErrorHandler';
import { mapCreditNoteLookupOption } from '../../features/sale/creditNote/creditNoteLookupOption';

export const useCreditNoteSaleLookup = () => {
    const dispatch = useDispatch();
    const { show: showAlert } = useAlerts();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { handle } = useApiErrorHandler();


    const { filteredDocuments, selectedSaleDetail, paginator } = useSelector(
        (state: RootState) => state.creditNoteSaleLookup
    );

    const searchEligibleSales = async (filters: CreditNoteSaleLookupFilter, page = 0, size = 10) => {
        if (!filters.customerId) return;
        try {
            showLoadingIndicator();
            const response = await CreditNoteSaleLookupService.searchEligibleSales(filters, page, size);
            console.log(response);

            const { data, statusCode } = response;

            if (statusCode === 200) {
                dispatch(setFilteredDocuments((data.content ?? []).map(mapCreditNoteLookupOption)));
                dispatch(setLookupPaginator({ number: data.number, size: data.size, totalElements: data.totalElements, totalPages: data.totalPages, first: data.first, last: data.last }));
            } else {
                showAlert('error', 'Error', 'No se pudieron obtener las ventas.');
            }
        } catch (error: any) {
            handle(error); // Manejo centralizado de errores
        } finally {
            hideLoadingIndicator();
        }
    };

    const fetchSaleDetail = async (saleId: number) => {
        try {
            const response = await CreditNoteSaleLookupService.getSaleDetail(saleId);
            const { data, statusCode } = response;

            console.log(response);
            if (statusCode === 200) {
                const creditFiscalId = data.id; // ✅ ID del crédito fiscal
                const products = data.products ?? [];

                // Guardamos el ID del crédito fiscal para futuras acciones
                if (creditFiscalId) {
                    dispatch(setCreditFiscalId(creditFiscalId));
                }

                const mappedProducts = mapToProductSummaries(products);
                dispatch(updateProductsFromBalance(mappedProducts));
            } else {
                showAlert('error', 'Error', 'No se pudo obtener el detalle de la venta.');
            }
        } catch (error: any) {
            handle(error);
        }
    };

    const clearCreditNoteSaleLookup = () => {
        dispatch(clearFilteredDocuments());
        dispatch(clearSelectedSaleDetail());
    };

    return {
        filteredDocuments,
        paginator,
        selectedSaleDetail,
        searchEligibleSales,
        fetchSaleDetail,
        clearCreditNoteSaleLookup,
    };
};
